/**
 * Wholesale Signup Form - Phone Country Picker
 * Uses libphonenumber-js for country data and validation
 */

(function() {
  'use strict';

  class PhoneCountryPicker {
    constructor(container) {
      this.container = container;
      this.phoneInput = container.querySelector('[data-phone-input]');
      this.dropdown = container.querySelector('[data-phone-dropdown]');
      this.searchInput = container.querySelector('[data-phone-search]');
      this.list = container.querySelector('[data-phone-list]');
      this.descEl = container.querySelector('[id$="-phone-country-desc"]');
      this.flagDisplay = container.querySelector('[data-phone-flag-display]');
      this.noResultsText = container.dataset.noResults || 'No countries found';
      
      this.selectedCountry = null;
      this.filteredCountries = [];
      this.allCountries = [];
      this.highlightedIndex = -1;
      this.form = null;
      
      this.init();
    }

    init() {
      if (!this.phoneInput || !this.dropdown || !this.list) return;

      this.loadCountryData();

      this.form = this.phoneInput.closest('form');
      if (this.form) {
        this.form.addEventListener('submit', (e) => this.validatePhone(e));
      }

      if (this.phoneInput.value.trim()) {
        this.detectCountryFromValue();
      }

      this.setupEventListeners();
    }

    loadCountryData() {
      const metadata = window.libphonenumber.getCountries();
      
      let countryNames;
      try {
        countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
      } catch (e) {
        console.warn('Intl.DisplayNames not supported');
      }
      
      this.allCountries = metadata.map(countryCode => {
        const countryCallingCode = window.libphonenumber.getCountryCallingCode(countryCode);
        const countryName = countryNames ? countryNames.of(countryCode) : countryCode;
        
        return {
          code: countryCode,
          name: countryName,
          dial: '+' + countryCallingCode,
          dialCode: countryCallingCode,
          flag: this.getFlagUrl(countryCode.toLowerCase())
        };
      });
      
      this.allCountries.sort((a, b) => a.name.localeCompare(b.name));
      this.filteredCountries = [...this.allCountries];
    }

    getFlagUrl(iso2) {
      return `https://cdn.jsdelivr.net/npm/flag-icons@7.2.3/flags/4x3/${iso2}.svg`;
    }

    setupEventListeners() {
      this.phoneInput.addEventListener('focus', () => {
        if (!this.selectedCountry) {
          this.showDropdown();
        }
      });
      
      this.phoneInput.addEventListener('mousedown', (e) => {
        if (!this.dropdown.hidden) {
          e.preventDefault();
          if (this.searchInput) {
            this.searchInput.focus();
          }
        }
      });
      
      this.phoneInput.addEventListener('keydown', (e) => {
        if (!this.dropdown.hidden && e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          if (this.searchInput) {
            this.searchInput.focus();
          }
        }
      });
      
      this.phoneInput.addEventListener('blur', () => {
        // Format phone number on blur
        this.formatPhoneNumber();
        
        setTimeout(() => {
          if (!this.dropdown.contains(document.activeElement) && 
              document.activeElement !== this.phoneInput) {
            this.hideDropdown();
          }
        }, 200);
      });
      
      this.phoneInput.addEventListener('input', () => {
        this.handlePhoneInputChange();
      });
      
      this.phoneInput.addEventListener('keydown', (e) => {
        if (this.selectedCountry) {
          const cursorPos = this.phoneInput.selectionStart;
          const dialCodeLength = this.selectedCountry.dial.length;
          
          if ((e.key === 'Backspace' || e.key === 'Delete') && cursorPos <= dialCodeLength) {
            e.preventDefault();
            this.phoneInput.setSelectionRange(dialCodeLength, dialCodeLength);
            return false;
          }
        }
      });
      
      if (this.searchInput) {
        this.searchInput.addEventListener('input', (e) => this.filterCountries(e.target.value));
        this.searchInput.addEventListener('keydown', (e) => this.handleSearchKeydown(e));
        
        this.searchInput.addEventListener('blur', () => {
          setTimeout(() => {
            const activeElement = document.activeElement;
            if (!this.dropdown.hidden && activeElement === this.phoneInput) {
              this.searchInput.focus();
            } else if (!this.dropdown.hidden && !this.container.contains(activeElement)) {
              this.hideDropdown();
            }
          }, 10);
        });
      }
      
      document.addEventListener('click', (e) => {
        if (!this.container.contains(e.target)) {
          this.hideDropdown();
        }
      });

      if (this.dropdown) {
        this.dropdown.addEventListener('mousedown', (e) => e.preventDefault());
      }
      
      if (this.flagDisplay) {
        this.flagDisplay.style.pointerEvents = 'auto';
        this.flagDisplay.style.cursor = 'pointer';
        this.flagDisplay.addEventListener('click', () => this.showDropdown());
        this.flagDisplay.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.showDropdown();
          }
        });
      }
    }

    detectCountryFromValue() {
      const value = this.phoneInput.value.trim();
      if (!value || !value.startsWith('+')) {
        this.hideFlagDisplay();
        this.selectedCountry = null;
        return;
      }

      try {
        const phoneNumber = window.libphonenumber.parsePhoneNumber(value);
        if (phoneNumber && phoneNumber.country) {
          const country = this.allCountries.find(c => c.code === phoneNumber.country);
          if (country) {
            this.selectedCountry = country;
            this.updateFlagDisplay(country);
          }
        }
      } catch (error) {
        const match = value.match(/^\+(\d{1,4})/);
        if (match) {
          const dialCode = match[1];
          const country = this.allCountries.find(c => c.dialCode === dialCode);
          if (country) {
            this.selectedCountry = country;
            this.updateFlagDisplay(country);
          }
        }
      }
    }

    handlePhoneInputChange() {
      const value = this.phoneInput.value.trim();
      
      if (!value) {
        this.hideFlagDisplay();
        this.selectedCountry = null;
        return;
      }
      
      if (value.startsWith('+')) {
        this.detectCountryFromValue();
      }
    }

    formatPhoneNumber() {
      if (!this.phoneInput) return;
      
      const value = this.phoneInput.value.trim();
      if (!value || !value.startsWith('+')) return;
      
      try {
        const phoneNumber = window.libphonenumber.parsePhoneNumber(value);
        if (phoneNumber && phoneNumber.isValid()) {
          // Format to international format
          this.phoneInput.value = phoneNumber.number;
        }
      } catch (error) {
        // Ignore formatting errors - leave as is
      }
    }

    updateFlagDisplay(country) {
      if (this.flagDisplay && country && country.flag) {
        this.flagDisplay.innerHTML = `<img src="${country.flag}" alt="${country.name}" style="width: 2rem; height: 1.5rem; object-fit: cover;">`;
        this.flagDisplay.style.display = 'block';
        
        if (this.phoneInput) {
          this.phoneInput.classList.add('has-flag');
        }
        
        if (this.descEl) {
          this.descEl.textContent = `Selected: ${country.name} ${country.dial}`;
        }
      }
    }
    
    hideFlagDisplay() {
      if (this.flagDisplay) {
        this.flagDisplay.style.display = 'none';
        this.flagDisplay.innerHTML = '';
      }
      
      if (this.phoneInput) {
        this.phoneInput.classList.remove('has-flag');
      }
    }

    showDropdown() {
      if (!this.dropdown || !this.phoneInput || !this.searchInput) return;
      
      if (this.dropdown.hidden) {
        this.dropdown.hidden = false;
        this.phoneInput.setAttribute('aria-expanded', 'true');
        this.phoneInput.setAttribute('aria-controls', this.dropdown.id);
        this.searchInput.value = '';
        this.filterCountries('');
        setTimeout(() => {
          if (this.searchInput) {
            this.searchInput.focus();
          }
        }, 50);
      }
    }

    hideDropdown() {
      if (!this.dropdown || !this.phoneInput) return;
      
      if (!this.dropdown.hidden) {
        this.dropdown.hidden = true;
        this.phoneInput.setAttribute('aria-expanded', 'false');
        this.phoneInput.removeAttribute('aria-controls');
        this.highlightedIndex = -1;
      }
    }

    filterCountries(query) {
      const searchTerm = query.toLowerCase().trim();
      
      if (searchTerm === '') {
        this.filteredCountries = [...this.allCountries];
      } else {
        this.filteredCountries = this.allCountries.filter(country => 
          country.name.toLowerCase().includes(searchTerm) ||
          country.dial.includes(searchTerm) ||
          country.code.toLowerCase().includes(searchTerm)
        );
      }
      
      this.highlightedIndex = -1;
      this.renderCountries();
    }

    renderCountries() {
      if (!this.list) return;
      
      this.list.innerHTML = '';
      
      if (this.filteredCountries.length === 0) {
        const li = document.createElement('li');
        li.className = 'wholesale-signup__phone-no-results';
        li.textContent = this.noResultsText;
        li.setAttribute('role', 'option');
        this.list.appendChild(li);
        return;
      }

      this.filteredCountries.forEach((country, index) => {
        const li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.setAttribute('data-country-code', country.code);
        li.setAttribute('data-dial-code', country.dial);
        li.setAttribute('tabindex', '-1');
        
        if (index === this.highlightedIndex) {
          li.setAttribute('aria-selected', 'true');
        }
        
        const flagImg = country.flag 
          ? `<img src="${country.flag}" alt="${country.name}" class="wholesale-signup__phone-flag-img" style="width: 2rem; height: 1.5rem; object-fit: cover;">`
          : '';
        
        li.innerHTML = `
          <span class="wholesale-signup__phone-flag" aria-hidden="true">${flagImg}</span>
          <span class="wholesale-signup__phone-country-name">${this.escapeHtml(country.name)}</span>
          <span class="wholesale-signup__phone-dial">${country.dial}</span>
        `;
        
        li.addEventListener('click', () => this.selectCountry(country));
        li.addEventListener('mouseenter', () => {
          this.highlightedIndex = index;
          this.updateHighlight();
        });
        
        this.list.appendChild(li);
      });
    }

    selectCountry(country) {
      if (!this.phoneInput) return;
      
      this.selectedCountry = country;
      
      const currentNumber = this.phoneInput.value.replace(/^\+[\d\s]+/, '').trim();
      
      this.phoneInput.value = country.dial + (currentNumber ? ' ' + currentNumber : '');
      
      this.updateFlagDisplay(country);
      
      this.hideDropdown();
      
      setTimeout(() => {
        if (this.phoneInput) {
          this.phoneInput.focus();
          const len = this.phoneInput.value.length;
          this.phoneInput.setSelectionRange(len, len);
        }
      }, 100);
    }
    
    validatePhone(e) {
      if (!this.phoneInput) return true;
      
      // Clear any previous custom validity first
      this.phoneInput.setCustomValidity('');
      
      const value = this.phoneInput.value.trim();
      
      if (!value) {
        e.preventDefault();
        this.phoneInput.setCustomValidity('Please enter a phone number');
        this.phoneInput.reportValidity();
        this.phoneInput.focus();
        return false;
      }

      try {
        const phoneNumber = window.libphonenumber.parsePhoneNumber(value);
        
        if (!phoneNumber.isValid()) {
          e.preventDefault();
          this.phoneInput.setCustomValidity('Please enter a valid phone number');
          this.phoneInput.reportValidity();
          this.phoneInput.focus();
          return false;
        }
        
        // Valid phone number - allow form submission
        return true;
      } catch (error) {
        e.preventDefault();
        this.phoneInput.setCustomValidity('Please enter a valid phone number with country code');
        this.phoneInput.reportValidity();
        this.phoneInput.focus();
        return false;
      }
    }

    handleSearchKeydown(e) {
      if (!this.list) return;
      
      const items = this.list.querySelectorAll('[role="option"]');
      
      switch(e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.highlightedIndex = Math.min(this.highlightedIndex + 1, items.length - 1);
          this.updateHighlight();
          items[this.highlightedIndex]?.focus();
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
          this.updateHighlight();
          if (this.highlightedIndex >= 0) {
            items[this.highlightedIndex]?.focus();
          } else if (this.searchInput) {
            this.searchInput.focus();
          }
          break;
          
        case 'Enter':
          e.preventDefault();
          if (this.highlightedIndex >= 0 && this.filteredCountries[this.highlightedIndex]) {
            const country = this.filteredCountries[this.highlightedIndex];
            this.selectCountry(country);
          } else if (this.filteredCountries.length === 1) {
            this.selectCountry(this.filteredCountries[0]);
          }
          break;
          
        case 'Escape':
          e.preventDefault();
          this.hideDropdown();
          setTimeout(() => {
            if (this.phoneInput) {
              this.phoneInput.focus();
            }
          }, 100);
          break;
      }
    }

    updateHighlight() {
      if (!this.list) return;
      
      const items = this.list.querySelectorAll('[role="option"]');
      items.forEach((item, index) => {
        if (index === this.highlightedIndex) {
          item.setAttribute('aria-selected', 'true');
          item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          item.setAttribute('aria-selected', 'false');
        }
      });
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  let initRetryCount = 0;
  const MAX_INIT_RETRIES = 50;

  function initPhonePickers() {
    if (typeof window.libphonenumber === 'undefined') {
      if (initRetryCount < MAX_INIT_RETRIES) {
        initRetryCount++;
        setTimeout(initPhonePickers, 100);
        return;
      } else {
        console.error('libphonenumber-js library failed to load');
        return;
      }
    }

    initRetryCount = 0;

    const containers = document.querySelectorAll('[data-wholesale-phone]');
    containers.forEach(container => {
      new PhoneCountryPicker(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPhonePickers);
  } else {
    initPhonePickers();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initPhonePickers);
  }
})();
